import React, { useEffect, useMemo, useState } from "react";
import {
  ContactLead,
  ContactLeadStatus,
  contactApi,
} from "../api/contactapi";
import "../styles/AdminContactLeadsPage.css";

const TAKE = 50;

const statusOptions: { value: ContactLeadStatus | "all"; label: string }[] = [
  { value: "all", label: "Tất cả" },
  { value: "new", label: "Mới gửi" },
  { value: "contacted", label: "Đã liên hệ" },
  { value: "quoted", label: "Đã báo giá" },
  { value: "closed", label: "Đã chốt" },
  { value: "cancelled", label: "Không tiềm năng" },
];

const leadStatusOptions: { value: ContactLeadStatus; label: string }[] = [
  { value: "new", label: "Mới gửi" },
  { value: "contacted", label: "Đã liên hệ" },
  { value: "quoted", label: "Đã báo giá" },
  { value: "closed", label: "Đã chốt" },
  { value: "cancelled", label: "Không tiềm năng" },
];

const getStatusLabel = (status: string) => {
  return leadStatusOptions.find((item) => item.value === status)?.label || status;
};

const getStatusClass = (status: string) => {
  if (status === "new") return "aclp-status-badge--new";
  if (status === "contacted") return "aclp-status-badge--contacted";
  if (status === "quoted") return "aclp-status-badge--quoted";
  if (status === "closed") return "aclp-status-badge--closed";
  return "aclp-status-badge--cancelled";
};

const toLocalDateTime = (value?: string | null) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("vi-VN");
};

const AdminContactLeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<ContactLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [status, setStatus] = useState<ContactLeadStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [noteById, setNoteById] = useState<Record<string, string>>({});

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(total / TAKE));
  }, [total]);

  const currentPage = useMemo(() => {
    return Math.floor(skip / TAKE) + 1;
  }, [skip]);

  const buildDateParams = () => {
    const fromUtc = fromDate
      ? new Date(`${fromDate}T00:00:00`).toISOString()
      : undefined;

    const toUtc = toDate
      ? new Date(`${toDate}T23:59:59`).toISOString()
      : undefined;

    return { fromUtc, toUtc };
  };

  const fetchLeads = async (nextSkip = 0) => {
    try {
      setLoading(true);
      setError("");
      setMessage("");

      const { fromUtc, toUtc } = buildDateParams();

      const data = await contactApi.getAdminContactLeads({
        skip: nextSkip,
        take: TAKE,
        status,
        search: search.trim() || undefined,
        fromUtc,
        toUtc,
      });

      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setSkip(nextSkip);
    } catch (err: any) {
  console.error(
    "GET CONTACT LEADS ERROR:",
    err?.response?.data || err
  );

  setError(
    err?.response?.data?.message ||
      err?.response?.data?.detail ||
      err?.message ||
      "Không thể tải lịch sử liên hệ."
  );
}finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    fetchLeads(0);
  };

  const handleUpdateStatus = async (
    lead: ContactLead,
    nextStatus: ContactLeadStatus
  ) => {
    try {
      setError("");
      setMessage("");

      const data = await contactApi.updateContactLeadStatus(lead.id, {
        status: nextStatus,
        internalNote: noteById[lead.id] ?? lead.internalNote ?? "",
      });

      const updatedLead = data.lead as ContactLead;

      setLeads((prev) =>
        prev.map((item) => (item.id === updatedLead.id ? updatedLead : item))
      );

      setMessage("Cập nhật trạng thái lead thành công.");
    } catch (err) {
      const apiError = err as Error;
      setError(apiError.message || "Không thể cập nhật trạng thái lead.");
    }
  };

  return (
    <div className="aclp-page">
      <div className="aclp-container">
        <div className="aclp-hero-card">
          <span className="aclp-eyebrow">Contact Lead Management</span>

          <div className="aclp-hero-content">
            <div>
              <h1>Lịch sử liên hệ khách hàng</h1>
              <p>
                Theo dõi thông tin khách gửi form Contact, ngày gửi, sản phẩm quan
                tâm và trạng thái xử lý để Sale/Admin thống kê sau này.
              </p>
            </div>

            <div className="aclp-total-card">
              <span>Tổng lead</span>
              <strong>{total}</strong>
            </div>
          </div>
        </div>

        <section className="aclp-filter-card">
          <div className="aclp-filter-grid">
            <label className="aclp-field aclp-field--search">
              <span>Tìm kiếm</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tên, SĐT, email, sản phẩm..."
              />
            </label>

            <label className="aclp-field">
              <span>Trạng thái</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ContactLeadStatus | "all")}
              >
                {statusOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="aclp-field">
              <span>Từ ngày</span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </label>

            <label className="aclp-field">
              <span>Đến ngày</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </label>

            <div className="aclp-filter-action">
              <button type="button" onClick={handleFilter}>
                Lọc
              </button>
            </div>
          </div>
        </section>

        {message && <div className="aclp-alert aclp-alert--success">{message}</div>}

        {error && <div className="aclp-alert aclp-alert--error">{error}</div>}

        <section className="aclp-table-card">
          {loading ? (
            <div className="aclp-empty-state">Đang tải lịch sử liên hệ...</div>
          ) : leads.length === 0 ? (
            <div className="aclp-empty-state">
              <h2>Chưa có lead</h2>
              <p>Khi khách gửi form Contact, dữ liệu sẽ hiện ở đây.</p>
            </div>
          ) : (
            <div className="aclp-table-wrap">
              <table className="aclp-table">
                <thead>
                  <tr>
                    <th>Ngày gửi</th>
                    <th>Thông tin khách</th>
                    <th>Nhu cầu</th>
                    <th>Sản phẩm</th>
                    <th>Nội dung</th>
                    <th>Trạng thái</th>
                    <th>Ghi chú nội bộ</th>
                  </tr>
                </thead>

                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id}>
                      <td className="aclp-date-cell">
                        <strong>{toLocalDateTime(lead.createdAtUtc)}</strong>
                        <p>Nguồn: {lead.source || "contact_page"}</p>
                      </td>

                      <td className="aclp-customer-cell">
                        <strong>{lead.fullName}</strong>
                        <a href={`tel:${lead.phone}`} className="aclp-phone-link">
                          {lead.phone}
                        </a>
                        {lead.email && (
                          <a href={`mailto:${lead.email}`} className="aclp-email-link">
                            {lead.email}
                          </a>
                        )}
                      </td>

                      <td>
                        <span className="aclp-need-badge">
                          {lead.need || "Tư vấn"}
                        </span>
                      </td>

                      <td>
                        {lead.productName ? (
                          <a
                            href={`/products/${lead.productSlug}`}
                            className="aclp-product-link"
                          >
                            {lead.productName}
                          </a>
                        ) : (
                          <span className="aclp-muted-text">Không gắn sản phẩm</span>
                        )}
                      </td>

                      <td className="aclp-message-cell">{lead.message || "-"}</td>

                      <td>
                        <span
                          className={`aclp-status-badge ${getStatusClass(
                            lead.status
                          )}`}
                        >
                          {getStatusLabel(lead.status)}
                        </span>

                        <select
                          value={lead.status}
                          onChange={(e) =>
                            handleUpdateStatus(
                              lead,
                              e.target.value as ContactLeadStatus
                            )
                          }
                          className="aclp-status-select"
                        >
                          {leadStatusOptions.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td>
                        <textarea
                          value={noteById[lead.id] ?? lead.internalNote ?? ""}
                          onChange={(e) =>
                            setNoteById((prev) => ({
                              ...prev,
                              [lead.id]: e.target.value,
                            }))
                          }
                          rows={3}
                          className="aclp-note-input"
                          placeholder="Ghi chú tình trạng xử lý..."
                        />
                        <button
                          type="button"
                          onClick={() => handleUpdateStatus(lead, lead.status)}
                          className="aclp-save-note-btn"
                        >
                          Lưu ghi chú
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="aclp-pagination">
          <span>
            Trang {currentPage}/{totalPages} · Hiển thị {leads.length}/{total} lead
          </span>

          <div className="aclp-pagination-actions">
            <button
              type="button"
              disabled={skip <= 0 || loading}
              onClick={() => fetchLeads(Math.max(0, skip - TAKE))}
            >
              Trước
            </button>
            <button
              type="button"
              disabled={skip + TAKE >= total || loading}
              onClick={() => fetchLeads(skip + TAKE)}
              className="aclp-next-btn"
            >
              Sau
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContactLeadsPage;